import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

export default function Profile() {
  const { currentUser, userProfile, logout, updateUserDocument } = useAuth();
  const navigate = useNavigate();

  const [addresses, setAddresses] = useState(userProfile?.addresses || []);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState({ type: "Home", street: "", city: "", state: "", zip: "" });
  const [saving, setSaving] = useState(false);

  // Modal states
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [addressToDeleteId, setAddressToDeleteId] = useState(null);
  const [addressToDefaultId, setAddressToDefaultId] = useState(null);

  async function confirmLogout() {
    await logout();
    navigate("/login");
  }

  const handleFormChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleAddAddress = async (e) => {
    e.preventDefault();
    if (!form.street || !form.city || !form.state || !form.zip) {
      toast.error("Please fill all required address fields.");
      return;
    }

    setSaving(true);
    const newAddress = {
      id: Date.now().toString(),
      type: form.type,
      street: form.street,
      city: form.city,
      state: form.state,
      zip: form.zip,
      isDefault: addresses.length === 0, // First address becomes default automatically
    };

    const newAddresses = [...addresses, newAddress];
    try {
      await updateUserDocument({ addresses: newAddresses });
      setAddresses(newAddresses);
      setShowAddForm(false);
      setForm({ type: "Home", street: "", city: "", state: "", zip: "" });
      toast.success("Address added successfully!");
    } catch (err) {
      toast.error("Failed to add address.");
    } finally {
      setSaving(false);
    }
  };

  const confirmSetDefault = async () => {
    if (!addressToDefaultId) return;

    const newAddresses = addresses.map((addr) => ({ ...addr, isDefault: addr.id === addressToDefaultId }));
    setAddresses(newAddresses);
    try {
      await updateUserDocument({ addresses: newAddresses });
      toast.success("Default address updated!");
    } catch (err) {
      toast.error("Failed to update default address.");
    } finally {
      setAddressToDefaultId(null);
    }
  };

  const confirmDeleteAddress = async () => {
    if (!addressToDeleteId) return;
    
    let newAddresses = addresses.filter((addr) => addr.id !== addressToDeleteId);
    // If we deleted the default address, and there are others left, make the first one default
    if (newAddresses.length > 0 && !newAddresses.find(a => a.isDefault)) {
      newAddresses[0].isDefault = true;
    }
    setAddresses(newAddresses);
    try {
      await updateUserDocument({ addresses: newAddresses });
      toast.success("Address deleted.");
    } catch (err) {
      toast.error("Failed to delete address.");
    } finally {
      setAddressToDeleteId(null);
    }
  };

  const getIconForType = (type) => {
    if (type === "Home") return <HomeOutlinedIcon sx={{ fontSize: 20 }} />;
    if (type === "Office") return <BusinessOutlinedIcon sx={{ fontSize: 20 }} />;
    return <LocationOnOutlinedIcon sx={{ fontSize: 20 }} />;
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-12 pb-20 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 sm:p-10"
        >
          <div className="flex items-center gap-4 mb-8 pb-8 border-b border-gray-100">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white text-2xl font-bold shadow-sm">
              {(userProfile?.displayName || currentUser?.email || "U")[0].toUpperCase()}
            </div>
            <div>
              <h1 className="text-3xl font-display font-bold text-gray-900">My Profile</h1>
              <p className="text-gray-500 text-sm mt-1">Manage your account information and saved addresses</p>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1.5">Display Name</p>
                <div className="text-gray-600 bg-gray-50 px-4 py-3 rounded-xl">
                  {userProfile?.displayName || currentUser?.displayName || "Not provided"}
                </div>
              </div>
              
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1.5">Email Address</p>
                <div className="text-gray-600 bg-gray-50 px-4 py-3 rounded-xl">
                  {userProfile?.email || currentUser?.email || "Not provided"}
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1.5">Phone Number</p>
                <div className="text-gray-600 bg-gray-50 px-4 py-3 rounded-xl">
                  {userProfile?.phone || "Not provided"}
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1.5">Account Role</p>
                <div className="h-full flex items-center">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-rose-50 text-rose-700 capitalize">
                    {userProfile?.role || "User"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Addresses Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 sm:p-10"
        >
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-100">
            <div>
              <h2 className="text-2xl font-display font-bold text-gray-900">Saved Addresses</h2>
              <p className="text-gray-500 text-sm mt-1">Manage delivery locations</p>
            </div>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="text-rose-600 bg-rose-50 hover:bg-rose-100 px-4 py-2 rounded-xl text-sm font-semibold transition-colors flex items-center gap-1.5"
            >
              <AddBoxOutlinedIcon sx={{ fontSize: 18 }} /> Add New
            </button>
          </div>

          <AnimatePresence>
            {showAddForm && (
              <motion.form 
                initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                onSubmit={handleAddAddress}
                className="overflow-hidden mb-8"
              >
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200 space-y-4">
                  <h3 className="font-semibold text-gray-900 text-lg">Add New Address</h3>
                  
                  <div className="flex gap-4">
                    {["Home", "Office", "Other"].map(type => (
                      <label key={type} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="type" value={type} checked={form.type === type} onChange={handleFormChange} className="text-rose-600 focus:ring-rose-500" />
                        <span className="text-sm text-gray-700">{type}</span>
                      </label>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                      <input type="text" name="street" value={form.street} onChange={handleFormChange} required className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500" placeholder="123 Main St, Apt 4B" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input type="text" name="city" value={form.city} onChange={handleFormChange} required className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                        <input type="text" name="state" value={form.state} onChange={handleFormChange} required className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                        <input type="text" name="zip" value={form.zip} onChange={handleFormChange} required className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-2">
                    <button type="button" onClick={() => setShowAddForm(false)} className="px-5 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-200 transition-colors">Cancel</button>
                    <button type="submit" disabled={saving} className="px-5 py-2 rounded-xl text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 transition-colors disabled:opacity-50">
                      {saving ? "Saving..." : "Save Address"}
                    </button>
                  </div>
                </div>
              </motion.form>
            )}
          </AnimatePresence>

          {addresses.length === 0 ? (
            <div className="text-center py-10 bg-gray-50 rounded-2xl border border-dashed border-gray-300">
              <LocationOnOutlinedIcon sx={{ fontSize: 40, color: "#9ca3af", mb: 1 }} />
              <p className="font-semibold text-gray-700">No saved addresses</p>
              <p className="text-sm text-gray-500 mt-1">Add an address for faster checkout</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {addresses.map((addr) => (
                <div key={addr.id} className={`relative p-5 sm:p-6 rounded-2xl border transition-all ${addr.isDefault ? "border-rose-300 bg-rose-50/30" : "border-gray-200 bg-white"}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                        {getIconForType(addr.type)}
                      </div>
                      <span className="font-semibold text-gray-900">{addr.type}</span>
                      {addr.isDefault && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-600 bg-rose-100 px-2 py-0.5 rounded-md ml-1">
                          <CheckCircleIcon sx={{ fontSize: 12 }} /> Default
                        </span>
                      )}
                    </div>
                    <button onClick={() => setAddressToDeleteId(addr.id)} className="text-gray-400 hover:text-red-500 transition-colors" title="Delete Address">
                      <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                    </button>
                  </div>
                  
                  <div className="mt-4 text-sm text-gray-600 leading-relaxed">
                    <p>{addr.street}</p>
                    <p>{addr.city}, {addr.state} {addr.zip}</p>
                  </div>

                  {!addr.isDefault && (
                    <button onClick={() => setAddressToDefaultId(addr.id)} className="mt-4 text-xs font-semibold text-rose-600 hover:underline">
                      Set as Default
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="pt-8 mt-8 border-t border-gray-100 flex justify-end">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowLogoutModal(true)}
              className="font-semibold bg-gray-900 text-white px-6 py-2.5 rounded-xl hover:bg-gray-800 transition-colors shadow-sm"
            >
              Log Out
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* Delete Address Confirmation Modal */}
      <AnimatePresence>
        {addressToDeleteId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 overflow-hidden"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Address?</h3>
              <p className="text-gray-600 text-sm mb-6">
                Are you sure you want to remove this address? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3 font-semibold text-sm">
                <button
                  onClick={() => setAddressToDeleteId(null)}
                  className="px-5 py-2.5 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteAddress}
                  className="px-5 py-2.5 rounded-xl text-white bg-rose-600 hover:bg-rose-700 transition-colors shadow-sm"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Set Default Confirmation Modal */}
      <AnimatePresence>
        {addressToDefaultId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 overflow-hidden"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Set as Default?</h3>
              <p className="text-gray-600 text-sm mb-6">
                Are you sure you want to make this your default shipping address?
              </p>
              <div className="flex justify-end gap-3 font-semibold text-sm">
                <button
                  onClick={() => setAddressToDefaultId(null)}
                  className="px-5 py-2.5 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSetDefault}
                  className="px-5 py-2.5 rounded-xl text-white bg-rose-600 hover:bg-rose-700 transition-colors shadow-sm"
                >
                  Confirm
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Logout Confirmation Modal */}
      <AnimatePresence>
        {showLogoutModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 overflow-hidden"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Ready to leave?</h3>
              <p className="text-gray-600 text-sm mb-6">
                Are you sure you want to log out of your account?
              </p>
              <div className="flex justify-end gap-3 font-semibold text-sm">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="px-5 py-2.5 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmLogout}
                  className="px-5 py-2.5 rounded-xl text-white bg-gray-900 hover:bg-gray-800 transition-colors shadow-sm"
                >
                  Log Out
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
